import React, { Fragment, useEffect, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import { Box, Divider, Grid, Typography } from "@mui/material";
import theme from "../../theme";
import moment from "moment";
import CommonEventIcon from "../../common/components/CommonEventIcon";
import CommonButton from "../../common/components/CommonButton";
import { Add, Close } from "@mui/icons-material";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { setLoading } from "../../features/authSlice";
import { useDispatch, useSelector } from "react-redux";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { getActivityLog } from "../../api/activityApi";
import { sendParentEmail } from "../../api/parentApi";

const colors = ["#E328AF", "#3F8CFF", "#00C48C"];

const RecentActivites = ({
    fromMentor,
    studentId
}) => {
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const [activity, setActivity] = useState([]);
    const [taskOpen, setTaskOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        title: '',
        notes: ''
    });

    const validateField = (name, value) => {
        switch (name) {
            case 'title':
                if (!value) return 'Title is required';
                return '';
            case 'notes':
                if (!value) return 'description is required';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            const response = await sendParentEmail({
                "subject": formData.title,
                "email_body": formData.notes,
                "student_id": studentId
            });
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({
                    loading: false
                }));
                setFormData({
                    title: '',
                    notes: ''
                });
                getHistory();
                setTaskOpen(false);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const getHistory = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getActivityLog(user?.student_id || studentId);
            setActivity(response?.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (user?.student_id || studentId) {
            getHistory();
        }
    }, [fromMentor, user, studentId]);

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={taskOpen}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Log Parent Notification</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setTaskOpen(false);
                                    setErrors({});
                                    setFormData({
                                        title: '',
                                        notes: ''
                                    });
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.5} mb={1}>
                        <Grid item md={12} xs={12}>
                            <CommonFormField
                                label="Title"
                                type="email"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                error={errors.title}
                                placeholder="Enter Title"
                            />
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <CommonFormField
                                label="Description"
                                type="email"
                                name="notes"
                                value={formData.notes}
                                onChange={handleInputChange}
                                error={errors.notes}
                                multiline
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label="Save"
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        handleClick={() => {
                            handleSubmit();
                        }}
                    />
                )}
            />
            <CommonCard
                borderRadius={2}
                headerContent={
                    <Fragment>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ padding: '13px' }}
                        >
                            <Box>
                                <Typography fontWeight={600}>{fromMentor ? 'Parent Communication & Alerts' : 'Recent Activites & Alerts'}</Typography>
                                {!fromMentor && (
                                    <Typography fontSize={14} color={theme.palette.background.dark}>Latest updates, notifications, and messages.</Typography>
                                )}
                            </Box>
                            {fromMentor && (
                                <CommonButton
                                    label="Log Parent Notification"
                                    height="35px"
                                    width="200px"
                                    marginBottom="0px"
                                    marginTop="0px"
                                    startIcon={<Add />}
                                    handleClick={() => setTaskOpen(true)}
                                />
                            )}
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Box
                        sx={{
                            height: '31vh',
                            overflow: 'auto'
                        }}
                    >
                        {activity.map((a, index) => {
                            const color = colors[index % colors.length];

                            return (
                                <Fragment key={index}>
                                    <Box
                                        display="flex"
                                        gap={1}
                                        alignItems="center"
                                        padding="10px"
                                    >
                                        <CommonEventIcon background={color} />
                                        <Box>
                                            <Typography fontSize={15} fontWeight="600">
                                                {a.title}
                                            </Typography>
                                            <Typography fontSize={13} color={theme.palette.background.dark}>
                                                {a.description}
                                            </Typography>
                                            <Typography
                                                fontSize={14}
                                                color={theme.palette.background.dark}
                                                sx={{ whiteSpace: "nowrap" }}
                                            >
                                                {moment(a.created_at).fromNow()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Fragment>
                            );
                        })}
                    </Box>
                }
                paddingBottom="0px"
                padding="13px"
                marginTop="15px"
            />
        </Fragment>
    )
}

export default RecentActivites;